import { useEffect, useMemo, useRef, useState, useContext } from "react";
import { Chessboard } from "react-chessboard";
import { RecoveryContext } from "../../App";
import { useNavigate } from "react-router-dom";
import { Chess } from "chess.js";

const PuzzleAnalysis = () => {
  const { currentFen, setCurrentFen, initialOrientation } =
    useContext(RecoveryContext);

  const [chessBoardPosition, setChessBoardPosition] = useState(currentFen);
  const [positionEvaluation, setPositionEvaluation] = useState(0);
  const [depth, setDepth] = useState(10);
  const [bestLine, setBestline] = useState([]);
  const [possibleMate, setPossibleMate] = useState("");
  const [moveHistory, setMoveHistory] = useState([currentFen]);
  const [currentMoveIndex, setCurrentMoveIndex] = useState(0);
  const workerRef = useRef(null);
  const navigate = useNavigate();

  const game = useMemo(() => {
    const g = new Chess();
    if (currentFen) {
      g.load(currentFen);
    }
    return g;
  }, [currentFen]);

  useEffect(() => {
    workerRef.current = new Worker("/stockfish.js");
    workerRef.current.postMessage("uci");

    workerRef.current.onmessage = (e) => {
      const message = e.data;
      if (message.includes("info")) {
        const positionEval = message.match(/score cp (-?\d+)/);
        const mateIn = message.match(/score mate (-?\d+)/);
        const depthMatch = message.match(/depth (\d+)/);
        const pvMatches = message.match(/ pv (.*)/g);

        if (positionEval && depthMatch) {
          setPositionEvaluation(
            ((game.turn() === "w" ? 1 : -1) * Number(positionEval[1])) / 100,
          );
          setDepth(Number(depthMatch[1]));
        }
        if (mateIn) {
          setPossibleMate(mateIn[1]);
        }

        if (pvMatches) {
          const topVariations = pvMatches
            .slice(0, 3)
            .map((pv) => pv.replace(" pv ", "").trim());
          setBestline(topVariations);
        }
      }
    };

    return () => {
      workerRef.current.postMessage("quit");
      workerRef.current.terminate();
    };
  }, []);
  function findBestMove() {
    workerRef.current.postMessage(`position fen ${chessBoardPosition}`);
    workerRef.current.postMessage(`go depth ${21} `);
  }

  const customLightSquareStyle = {
    backgroundColor: "#AE8FD6",
  };

  const customDarkSquareStyle = {
    backgroundColor: "#4F3B7B",
  };
  function onDrop(sourceSquare, targetSquare, piece) {
    const moveDetails = {
      from: sourceSquare,
      to: targetSquare,
      promotion: piece[1].toLowerCase() ?? "q",
    };

    const possibleMoves = game.moves({ verbose: true });
    const isValidMove = possibleMoves.some(
      (m) => m.from === moveDetails.from && m.to === moveDetails.to,
    );

    if (!isValidMove) return false;

    const move = game.move(moveDetails);
    if (move === null) return false;

    setPossibleMate("");
    const newFen = game.fen();
    setChessBoardPosition(newFen);
    setCurrentFen(newFen);
    setMoveHistory((prevHistory) => {
      const newHistory = [
        ...prevHistory.slice(0, currentMoveIndex + 1),
        newFen,
      ];
      sessionStorage.setItem("moveHistory", JSON.stringify(newHistory));
      return newHistory;
    });
    setCurrentMoveIndex((prevIndex) => {
      const newIndex = prevIndex + 1;
      sessionStorage.setItem("currentMoveIndex", JSON.stringify(newIndex));
      return newIndex;
    });

    workerRef.current.postMessage("stop");
    setBestline("");

    if (game.game_over || game.in_draw) {
      return false;
    }

    return true;
  }
  useEffect(() => {
    if (!game.game_over || game.in_draw) {
      findBestMove();
    }
  }, [chessBoardPosition]);

  function goToPreviousMove() {
    if (currentMoveIndex > 0) {
      const previousFen = moveHistory[currentMoveIndex - 1];
      game.load(previousFen);
      setChessBoardPosition(previousFen);
      setCurrentFen(previousFen);
      setCurrentMoveIndex((prevIndex) => {
        const newIndex = prevIndex - 1;
        sessionStorage.setItem("currentMoveIndex", JSON.stringify(newIndex));
        return newIndex;
      });
    }
  }

  function goToNextMove() {
    if (currentMoveIndex < moveHistory.length - 1) {
      const nextFen = moveHistory[currentMoveIndex + 1];
      game.load(nextFen);
      setChessBoardPosition(nextFen);
      setCurrentFen(nextFen);
      setCurrentMoveIndex((prevIndex) => {
        const newIndex = prevIndex + 1;
        sessionStorage.setItem("currentMoveIndex", JSON.stringify(newIndex));
        return newIndex;
      });
    }
  }

  const bestMove = bestLine.length > 0 ? bestLine[0].split(" ")[0] : null;
  return (
    <div className="puzzle-analysis-div responsive-page-shell">
      <div
        className="puzzle-board-casual"
        style={{
          width: "min(770px, 100%)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          position: "relative",
          margin: "0",
        }}
      >
        <Chessboard
          id="AnalysisBoard"
          position={chessBoardPosition}
          onPieceDrop={onDrop}
          customLightSquareStyle={customLightSquareStyle}
          customDarkSquareStyle={customDarkSquareStyle}
          boardOrientation={initialOrientation}
          customArrows={
            bestMove
              ? [
                  [
                    bestMove.substring(0, 2),
                    bestMove.substring(2, 4),
                    "rgb(60, 94, 229)",
                  ],
                ]
              : undefined
          }
        />
      </div>

      <div className="puzzles-div-analysis-backbutton">
        <div>
          <h3>Analysis:</h3>
        </div>
        <h4>
          Position Evaluation:{" "}
          {possibleMate ? `#${possibleMate}` : positionEvaluation}
          {"; "}Depth: {depth}
        </h4>
        <h5>
          {bestLine.length > 0 ? (
            bestLine.map((line, index) => (
              <div key={index}>
                Variation {index + 1}: <i>{line.slice(0, 40)}</i> ...
              </div>
            ))
          ) : (
            <i>No variations found</i>
          )}
        </h5>
        <button
          className="back-to-puzzle-casual"
          onClick={() => navigate("/CasualPuzzles")}
        >
          Back to puzzles
        </button>
        <button onClick={goToPreviousMove} disabled={currentMoveIndex === 0}>
          Previous Move
        </button>
        <button
          onClick={goToNextMove}
          disabled={currentMoveIndex === moveHistory.length - 1}
        >
          Next Move
        </button>
      </div>
    </div>
  );
};

export default PuzzleAnalysis;
